import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  TextInput,
  Group,
  Container,
  Title,
  Text,
  Card,
  ActionIcon,
  Tooltip,
  Loader,
  Flex,
  Paper,
  Notification,
  Modal,
  Checkbox,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { deleteDistributor, getAllDistributors } from "@utils/api-communication/RestApiCli";
import { IconPlus, IconEdit, IconTrash, IconCheck, IconX } from "@tabler/icons-react";

interface DistributorConfig {
  language: string;
  forcedPassChangeFirstLogin: boolean;
}

interface Distributor {
  name: string;
  country: string[];
  emails: string[];
  phone?: string;
  distId: string;
  orgIds?: string[];
  config?: DistributorConfig;
  createdAt?: Date;
}

const DistributorManagement: React.FC = () => {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [filteredDistributors, setFilteredDistributors] = useState<Distributor[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Distributor | null>(null);
  const [deactivateDistAccounts, setDeactivateDistAccounts] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();

  // get all distributors on component mount
  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = () => {
    setIsLoading(true);
    getAllDistributors(
      (response) => {
        const data = response.data;
        setDistributors(data);
        setFilteredDistributors(data);
        setIsLoading(false);
      },
      (error) => {
        console.error("Failed to fetch distributors", error);
        setIsLoading(false);
        showNotification("error", "Failed to fetch distributors");
      },
    );
  };

  // Filter distributors when search term changes
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredDistributors(distributors);
    } else {
      const lowercaseSearch = searchTerm.toLowerCase();
      const filtered = distributors.filter(
        (dist) =>
          dist.name.toLowerCase().includes(lowercaseSearch) ||
          dist.emails.some((email) => email.toLowerCase().includes(lowercaseSearch)) ||
          (dist.phone && dist.phone.includes(searchTerm)) ||
          dist.country.some((country) => country.toLowerCase().includes(lowercaseSearch)),
      );
      setFilteredDistributors(filtered);
    }
  }, [searchTerm, distributors]);

  const handleAddClick = () => {
    navigate(`/distributor?create=true`);
  };

  const handleEditClick = (distributor: Distributor) => {
    navigate(`/distributor?distId=${encodeURIComponent(distributor.distId)}`);
  };

  const handleDeleteClick = (distributor: Distributor) => {
    setDeleteTarget(distributor);
    setDeactivateDistAccounts(false);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    deleteDistributor(
      deleteTarget.distId,
      deactivateDistAccounts,
      () => {
        setIsDeleting(false);
        setDeleteTarget(null);
        setDeactivateDistAccounts(false);
        showNotification("success", `Deleted distributor ${deleteTarget.distId}`);
        fetchDistributors();
      },
      (error) => {
        console.error("Failed to delete distributor", error);
        setIsDeleting(false);
        showNotification(
          "error",
          error?.response?.data?.message || `Failed to delete distributor ${deleteTarget.distId}`,
        );
      },
    );
  };

  const showNotification = (type: "success" | "error", message: string) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  return (
    <Container size="xl" px="md">
      <Modal
        opened={!!deleteTarget}
        onClose={() => {
          if (isDeleting) return;
          setDeleteTarget(null);
          setDeactivateDistAccounts(false);
        }}
        title="Delete Distributor"
        centered
      >
        <Text mb="md">
          Are you sure you want to delete distributor{" "}
          <Text span fw={700}>
            {deleteTarget?.distId}
          </Text>
          ?
        </Text>
        <Checkbox
          label="Deactivate distAccounts"
          description={
            deactivateDistAccounts
              ? "Organizations and users in this distributor will be deactivated."
              : "Organizations and users will stay active and their distId will be reassigned to the admin."
          }
          checked={deactivateDistAccounts}
          onChange={(event) => setDeactivateDistAccounts(event.currentTarget.checked)}
          disabled={isDeleting}
          mb="md"
        />
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => {
              setDeleteTarget(null);
              setDeactivateDistAccounts(false);
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleConfirmDelete} loading={isDeleting}>
            Delete
          </Button>
        </Group>
      </Modal>

      {notification && (
        <Notification
          color={notification.type === "success" ? "green" : "red"}
          title={notification.type === "success" ? "Success" : "Error"}
          icon={notification.type === "success" ? <IconCheck size={18} /> : <IconX size={18} />}
          onClose={() => setNotification(null)}
          withCloseButton
          mb="md"
        >
          {notification.message}
        </Notification>
      )}

      <Paper shadow="xs" p="md" withBorder mb="lg">
        <Flex justify="space-between" align="center" mb="md">
          <Title order={2}>Distributor Management</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={handleAddClick}>
            Add Distributor
          </Button>
        </Flex>

        <TextInput
          placeholder="Search distributors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          mb="md"
        />

        {isLoading ? (
          <Flex justify="center" align="center" h={200}>
            <Loader />
          </Flex>
        ) : (
          <Card withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Emails</Table.Th>
                  <Table.Th>Phone</Table.Th>
                  <Table.Th>Country</Table.Th>
                  <Table.Th>Language</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredDistributors.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text ta="center" py="md" fz="sm" c="dimmed">
                        {searchTerm ? "No matching distributors found" : "No distributors found"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredDistributors.map((dist) => (
                    <Table.Tr key={dist.distId}>
                      <Table.Td>{dist.name}</Table.Td>
                      <Table.Td>{dist.emails.join(", ")}</Table.Td>
                      <Table.Td>{dist.phone || "-"}</Table.Td>
                      <Table.Td>{dist.country.join(", ")}</Table.Td>
                      <Table.Td>{dist.config?.language || "en"}</Table.Td>
                      <Table.Td>
                        <Group>
                          <Tooltip label="Edit">
                            <ActionIcon
                              size="sm"
                              color="blue"
                              variant="subtle"
                              onClick={() => handleEditClick(dist)}
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Delete">
                            <ActionIcon
                              size="sm"
                              color="red"
                              variant="subtle"
                              onClick={() => handleDeleteClick(dist)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Paper>
    </Container>
  );
};

export default DistributorManagement;
